/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 22.10.17.
 */

import update from "immutability-helper";
import _ from "underscore";
import {InternalErrorException} from "../Exceptions";
import * as helpers from "./Helper";

/**
 * Class for handling data changes. All changes will be applied to the data without changing the original source.
 * Class is using helpers when applying data cahnges. Helpers can be described in JSON UI Schema file and they
 * concretizise how exactly should data changes applied.
 */
class DataManager {
    /**
     * Create data manager object
     * @param {Schema} schema Schema associated with the data
     * @param {String} helper Helper object name
     */
    constructor(schema, helper) {
        /**
         * Schema associated with the data
         * @type {Schema}
         */
        this.schema = schema;

        this.helper = helper;

        /**
         * Array of error messages
         * @type {?String[]}
         */
        this.errors = null;
    }

    /**
     * Replaces field value
     * @param {Object} data Data to where apply the value
     * @param {string} field Field path
     * @param {string} action Action to perform with the value. One of: "insert", "update", "delete"
     * @param {Object} value New value for the field
     * @returns {Object} Modified data
     */
    applyFieldChange(data, field, action, value) {
        this.errors = null;
        /**
         * @type {BaseHelper}
         */
        let helper = this.getHelper(field);
        if (!helper) throw new InternalErrorException("Failed to change field value");
        data = this.createFieldIfNeeded(data, field);
        let updateSpec = this.makeFieldUpdateSpec(helper, field, action, value);
        return update(data, updateSpec);
    }

    /**
     * Returns true if applyAction() method fails
     * @returns {boolean}
     */
    isError() {
        return this.errors !== null;
    }

    /**
     * Returns error list if applyAction() was failed. Returns null if there were no errors
     * @returns {?String} Array of error messages
     */
    getErrors() {
        return this.errors;
    }

    /**
     * Assures that specified field exists also in data, modifing data if needed
     * @param {Object} data
     * @param {string} field
     * @returns {Object}
     */
    createFieldIfNeeded(data, field) {
        if (field == null || field === "") return;
        if (!_.isArray(field)) field = field.split("/");
        else field = field.slice();

        let schema = this.schema;

        if (data == null) {
            switch (schema.getType()) {
                case "object":
                    data = {};
                    break;
                case "array":
                    data = [];
                default:
                    data = null;
                    break;
            }
        }

        let d = data;

        let rootSpec = {};
        let spec = rootSpec;

        while (field.length > 0) {
            let f = field.shift();

            let dataField = this.getDataField(f);
            if (!d[dataField]) {
                let value = this.createValueForField(schema.field(f), field);
                switch (schema.getType()) {
                    case "object":
                        spec["$merge"] = {[dataField]: value};
                        break;
                    case "array":
                        spec["$splice"] = [[dataField, 1, value]];
                    default:
                        spec["$set"] = value;
                        break;
                }
                return update(data, rootSpec);
            }

            schema = schema.field(f);
            spec[dataField] = {};
            spec = spec[dataField];
            d = d[dataField];
        }
        return data;
    }

    createValueForField(schema, field) {
        let data = null;
        switch (schema.getType()) {
            case "object":
                data = {};
                break;
            case "array":
                data = [];
                break;
            default:
                data = null;
                break;
        }
        if (field == null) return data;
        let d = data;
        for (let f of field) {
            let dataField = this.getDataField(f);
            schema = schema.field(f);
            switch (schema.getType()) {
                case "object":
                    d[dataField] = {};
                    break;
                case "array":
                    d[dataField] = [];
                    break;
                default:
                    d[dataField] = null;
                    break;
            }
            d = d[dataField];
        }
        return data;
    }

    /**
     * Creates field update spec
     * @protected
     * @param {BaseHelper} helper Helper object
     * @param {string} field Updatable field
     * @param {string} action Update action
     * @param {Object} value New value for the field
     * @return {Object} Update spec
     */
    makeFieldUpdateSpec(helper, field, action, value) {
        /**
         *
         * @type {Schema}
         */
        let fieldSchema = this.schema.field(field);

        if (field == null) field = [];
        if (!_.isArray(field)) field = field.split("/");

        let rootSpec = {};
        let spec = rootSpec;
        for (let f of field) {
            let specField = this.getDataField(f);

            spec[specField] = {};
            spec = spec[specField];
        }

        spec["$apply"] = function (originalValue) {
            let newValue = helper.apply(action, originalValue, value, fieldSchema);
            this.errors = helper.getErrors();
            return newValue;
        }.bind(this);
        return rootSpec;
    }

    getDataField(field) {
        let dataField = field;
        if (field === "*") {
            dataField = 0;
        }
        else if (field.startsWith("[") && field.endsWith("]")) {
            dataField = Number.parseInt(field.substring(1, field.length-1));
        }
        return dataField;
    }

    /**
     * @protected
     * @param {string} field Field path
     * @returns {BaseHelper} Helper object
     */
    getHelper(field) {
        // TODO: Realiseerida UI Schemast helperi parameetrite tuvastamine
        return this.createHelperObject((this.helper ? this.helper : "Simple"), null);
    }

    createHelperObject(helperName, helperOptions) {
        if (!helpers[helperName]) return null;
        let helperClass = helpers[helperName];
        return new helperClass(helperOptions);
    }
}

export default DataManager;
